/**
 * Product Card Component - Vanilla JS
 * Handles add-to-cart functionality without Alpine.js dependency
 */

// State tracking for each card
const cardStates = new Map();

/**
 * Get card data from data attributes
 */
function getCardData(card) {
  const variantsRaw = card.dataset.variants || '[]';
  let variants = [];

  try {
    // Handle HTML entity encoded JSON
    const decoded = variantsRaw
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    variants = JSON.parse(decoded);
  } catch (e) {
    console.error('Error parsing variants:', e);
  }

  return {
    productId: parseInt(card.dataset.productId, 10) || 0,
    variantId: parseInt(card.dataset.variantId, 10) || 0,
    hasVariants: card.dataset.hasVariants === 'true',
    variants,
    productUrl: card.dataset.productUrl || '#',
    productTitle: card.dataset.productTitle || '',
    productImage: card.dataset.productImage || ''
  };
}

/**
 * Update card UI based on quantity
 */
function updateCardUI(card, quantity) {
  const addBtn = card.querySelector('[data-action="add"]');
  const stepper = card.querySelector('[data-stepper]');
  const qtyDisplay = card.querySelector('[data-quantity]');

  if (!addBtn || !stepper) return;

  if (quantity > 0) {
    addBtn.style.display = 'none';
    stepper.classList.add('is-visible');
    if (qtyDisplay) qtyDisplay.textContent = quantity;
  } else {
    addBtn.style.display = 'flex';
    stepper.classList.remove('is-visible');
  }
}

/**
 * Set loading state on card
 */
function setLoading(card, isLoading) {
  const addBtn = card.querySelector('[data-action="add"]');
  const spinner = card.querySelector('[data-spinner]');
  const icon = addBtn?.querySelector('.product-card__add-icon');
  const stepperBtns = card.querySelectorAll('.product-card__stepper-btn');

  if (addBtn) {
    addBtn.disabled = isLoading;
    addBtn.classList.toggle('is-loading', isLoading);
  }

  if (spinner) {
    spinner.style.display = isLoading ? 'block' : 'none';
  }

  if (icon) {
    icon.style.opacity = isLoading ? '0' : '1';
  }

  stepperBtns.forEach(btn => {
    btn.disabled = isLoading;
  });

  // Store loading state
  cardStates.set(card, { ...cardStates.get(card), isLoading });
}

/**
 * Get quantity for a product from cart
 */
function getQuantityFromCart(variants) {
  if (!window.Alpine) return 0;

  const cart = window.Alpine.store('cart');
  if (!cart || !cart.cartData || !cart.cartData.items) return 0;

  let totalQty = 0;
  for (const variant of variants) {
    const item = cart.cartData.items.find(i => i.variant_id === variant.id);
    if (item && typeof item.quantity === 'number') {
      totalQty += item.quantity;
    }
  }
  return totalQty;
}

/**
 * Find line index (1-based) for variant in cart
 */
function findLineIndex(variantId) {
  if (!window.Alpine) return null;

  const cart = window.Alpine.store('cart');
  if (!cart || !cart.cartData || !cart.cartData.items) return null;

  const index = cart.cartData.items.findIndex(item => item.variant_id === variantId);
  return index >= 0 ? index + 1 : null;
}

/**
 * Add item to cart via Shopify API
 */
async function addToCart(variantId, quantity = 1) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity })
  });

  if (!response.ok) throw new Error('Failed to add to cart');
  return response.json();
}

/**
 * Update cart item quantity
 */
async function updateCartItem(line, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line, quantity })
  });

  if (!response.ok) throw new Error('Failed to update cart');
  return response.json();
}

/**
 * Sync all cards with cart data
 */
function syncAllCards() {
  document.querySelectorAll('[data-product-card]').forEach(card => {
    const data = getCardData(card);
    const quantity = getQuantityFromCart(data.variants);
    updateCardUI(card, quantity);
  });
}

/**
 * Handle add to cart action
 */
async function handleAdd(card) {
  const state = cardStates.get(card) || {};
  if (state.isLoading) return;

  const data = getCardData(card);

  // Products with variants open modal
  if (data.hasVariants) {
    if (window.Alpine) {
      const variantModal = window.Alpine.store('variantModal');
      if (variantModal) {
        variantModal.open({
          id: data.productId,
          title: data.productTitle,
          image: data.productImage,
          variants: data.variants
        });
      }
    }
    return;
  }

  // Single variant: add directly
  setLoading(card, true);
  try {
    await addToCart(data.variantId, 1);

    // Refresh cart store if Alpine is available
    if (window.Alpine) {
      const cart = window.Alpine.store('cart');
      if (cart) await cart.fetchCart();
    }

    // Update UI
    syncAllCards();
  } catch (error) {
    console.error('Error adding to cart:', error);
  } finally {
    setLoading(card, false);
  }
}

/**
 * Handle increment action
 */
async function handleIncrement(card) {
  const state = cardStates.get(card) || {};
  if (state.isLoading) return;

  const data = getCardData(card);

  // Products with variants open modal
  if (data.hasVariants) {
    handleAdd(card);
    return;
  }

  // Single variant: add one more
  setLoading(card, true);
  try {
    await addToCart(data.variantId, 1);

    if (window.Alpine) {
      const cart = window.Alpine.store('cart');
      if (cart) await cart.fetchCart();
    }

    syncAllCards();
  } catch (error) {
    console.error('Error incrementing quantity:', error);
  } finally {
    setLoading(card, false);
  }
}

/**
 * Handle decrement action
 */
async function handleDecrement(card) {
  const state = cardStates.get(card) || {};
  if (state.isLoading) return;

  const data = getCardData(card);
  const currentQty = getQuantityFromCart(data.variants);

  if (currentQty <= 0) return;

  // Products with variants: open modal to manage quantities
  if (data.hasVariants) {
    if (window.Alpine) {
      const variantModal = window.Alpine.store('variantModal');
      if (variantModal) {
        variantModal.open({
          id: data.productId,
          title: data.productTitle,
          image: data.productImage,
          variants: data.variants
        });
      }
    }
    return;
  }

  setLoading(card, true);
  try {
    const line = findLineIndex(data.variantId);

    if (line === null) {
      console.error('Could not find item in cart');
      return;
    }

    const newQty = currentQty - 1;
    await updateCartItem(line, newQty);

    if (window.Alpine) {
      const cart = window.Alpine.store('cart');
      if (cart) await cart.fetchCart();
    }

    syncAllCards();
  } catch (error) {
    console.error('Error decrementing quantity:', error);
  } finally {
    setLoading(card, false);
  }
}

/**
 * Initialize product cards with event delegation
 */
function initProductCards() {
  // Event delegation for all product card actions
  document.addEventListener('click', (e) => {
    const actionBtn = e.target.closest('[data-action]');
    if (!actionBtn) return;

    const card = actionBtn.closest('[data-product-card]');
    if (!card) return;

    e.preventDefault();
    e.stopPropagation();

    const action = actionBtn.dataset.action;

    switch (action) {
      case 'add':
        handleAdd(card);
        break;
      case 'increment':
        handleIncrement(card);
        break;
      case 'decrement':
        handleDecrement(card);
        break;
    }
  });

  // Listen for cart updates to sync UI
  document.addEventListener('cart:updated', () => {
    syncAllCards();
  });

  // Initial sync after a short delay to ensure cart is loaded
  setTimeout(syncAllCards, 100);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCards);
} else {
  initProductCards();
}

// Export for potential external use
export { syncAllCards, initProductCards };
